export type FormKind = 'manifest' | 'flight-card' | 'certification-flight' | 'project-flight';

export type SaveResult =
  | { mode: 'server'; referenceId: string }
  | { mode: 'error'; message: string };

export type TeamAccountInput = {
  teamName: string;
  school: string;
  teamEmail: string;
  teamDiscordChannel: string;
  primaryTeamLeadName: string;
  primaryTeamLeadEmail: string;
  primaryTeamLeadPhone: string;
  primaryTeamLeadDiscordUsername: string;
  primaryTeamLeadExpiration: string;
  graduationYear: number | null;
  clubexpressMemberNumber: string;
  crpMembershipStatus: string;
  membershipStart: string;
  membershipEnd: string;
  notes: string;
  facultyAdvisor: string;
  schoolBillingContact: string;
};

export type TeamAdditionalLeadInput = {
  fullName: string;
  email: string;
  phone: string;
  discordUsername: string;
  expirationDate: string;
};

export type TeamAdditionalLead = TeamAdditionalLeadInput & {
  id: string;
  updatedAt: string;
};

export type TeamAccount = TeamAccountInput & {
  id: string;
  additionalLeads: TeamAdditionalLead[];
  isSystem: boolean;
  teamLeadVerifiedAt: string;
  teamLeadVerifiedByEmail: string;
  updatedAt: string;
};

export type TeamMemberInput = {
  fullName: string;
  email: string;
  phone: string;
  discordUsername: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  graduationYear: number | null;
  certificationLevel: string;
  narNumber: string;
  narExpiration: string;
  traNumber: string;
  traExpiration: string;
  l2ExamPassed: boolean | null;
  l2ExamDate: string;
  notes: string;
  memberNumber: string;
  membershipStatus: 'Active' | 'Inactive' | 'Graduated' | 'Needs review';
  membershipExpiration: string;
  assignedTeamId?: string;
};

export type TeamMember = TeamMemberInput & {
  id: string;
  updatedAt: string;
};

export type TeamDirectoryAccess = {
  role: 'admin' | 'lead' | 'lco';
  email: string;
  isSuperAdmin: boolean;
  team: TeamAccount | null;
};

export type AdminUser = {
  id: string;
  email: string;
  superAdmin: boolean;
  configured: boolean;
  managed: boolean;
  removable: boolean;
  createdByEmail: string;
  createdAt: string;
};

export type LaunchEventInput = {
  name: string;
  startDate: string;
  endDate: string;
  status: 'Scheduled' | 'Moved' | 'Completed' | 'Cancelled';
  acceptingSubmissions: boolean;
};

export type LaunchEvent = LaunchEventInput & {
  id: string;
  updatedAt: string;
};

export type LaunchSummaryCounts = {
  flightSubmissions: number;
  standaloneFlightCards: number;
  projectFlights: number;
  certificationFlights: number;
  l1Certifications: number;
  l2Certifications: number;
  l3Certifications: number;
  manifests: number;
  manifestFlyers: number;
  manifestStandardFlights: number;
  manifestL1CertFlights: number;
  manifestL2CertFlights: number;
  manifestProjectFlights: number;
  spectators: number;
};

export type LaunchEventSummary = LaunchEvent & LaunchSummaryCounts;

export type LaunchDaySummary = {
  overall: LaunchSummaryCounts & {
    launchEvents: number;
    acceptingSubmissions: number;
    unassignedSubmissions: number;
  };
  events: LaunchEventSummary[];
};

export type FlightReviewInput = {
  flightResult: 'Not recorded' | 'Successful deployment' | 'Main at apogee' | 'Ballistic' | 'Drogue / no main' | 'Other';
  landingLocation: 'Not recorded' | 'On field' | 'Off field';
  notes: string;
};

export type CertificationChecklistStatus = 'Pending' | 'Approved' | 'Failed' | 'N/A';

export type CertificationChecklistItemInput = {
  id: string;
  status: CertificationChecklistStatus;
  notes: string;
};

export type CertificationChecklistItem = CertificationChecklistItemInput & {
  section: string;
  label: string;
};

export type CertificationReviewInput = {
  decision: 'Pending' | 'Approved' | 'Failed' | 'Incomplete' | 'Not attempted';
  notes: string;
  signerId: string;
  checklist: CertificationChecklistItemInput[];
};

export type FlightReviewRecord = Omit<FlightReviewInput, 'notes'> & {
  flightNotes: string;
  flightReviewedByEmail: string;
  flightReviewedAt: string;
};

export type SubmissionReview = FlightReviewRecord & {
  certificationDecision: CertificationReviewInput['decision'];
  certificationNotes: string;
  certificationChecklist: CertificationChecklistItem[];
  certificationSignerId: string;
  certificationSignerName: string;
  certificationDecidedByEmail: string;
  certificationDecidedAt: string;
};

export type LaunchSubmissionSummary = {
  referenceId: string;
  formType: Exclude<FormKind, 'manifest'>;
  formTitle: string;
  submittedAt: string;
  flyerName: string;
  flyerEmail: string;
  rocketName: string;
  certAttempt: string;
  requiredCapability: string;
  review: SubmissionReview;
};

export type LaunchFlightCardSummary = Omit<LaunchSubmissionSummary, 'requiredCapability' | 'review'> & {
  review: FlightReviewRecord;
};

export type SubmissionReviewHistoryEntry = {
  action: string;
  actorEmail: string;
  details: Record<string, unknown>;
  createdAt: string;
};

export type LaunchSubmissionDetail = LaunchSubmissionSummary & {
  payload: Record<string, unknown>;
  history: SubmissionReviewHistoryEntry[];
};

export type LaunchFlightCardDetail = LaunchFlightCardSummary & {
  payload: Record<string, unknown>;
  history: SubmissionReviewHistoryEntry[];
};

export type LaunchSubmissionList = {
  submissions: LaunchSubmissionSummary[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type LaunchFlightCardList = Omit<LaunchSubmissionList, 'submissions'> & {
  submissions: LaunchFlightCardSummary[];
};

export type CertificationSigner = {
  id: string;
  fullName: string;
  qualifications: string;
  permanentCapabilities: string[];
  effectiveCapabilities: string[];
  temporaryPrefect: boolean;
};

export type TemporaryCertificationAuthorization = {
  signerId: string;
  signerName: string;
  notes: string;
  authorizedByEmail: string;
  updatedAt: string;
};

export type CertificationSignerDirectory = {
  signers: CertificationSigner[];
  temporaryAuthorization: TemporaryCertificationAuthorization | null;
};

export type SchoolDirectorySummary = {
  school: string;
  teamId: string;
  teamNames: string[];
  membershipStatus: string;
  teamCount: number;
  individualCount: number;
  activeIndividuals: number;
  inactiveIndividuals: number;
  graduatedIndividuals: number;
  needsReviewIndividuals: number;
};

export type TeamDirectorySummary = {
  overall: {
    schoolCount: number;
    activeSchoolCount: number;
    inactiveSchoolCount: number;
    individualCount: number;
    activeIndividuals: number;
    inactiveIndividuals: number;
    graduatedIndividuals: number;
    needsReviewIndividuals: number;
    unassignedIndividuals: number;
  };
  schools: SchoolDirectorySummary[];
};

export type CsvImportPreview = {
  id: string;
  validRows: number;
  newMembers: number;
  updatedMembers: number;
  errors: Array<{ rowNumber: number; message: string }>;
  expiresAt: string;
};

export type ManifestDirectoryMember = {
  fullName: string;
  email: string;
  narTra: string;
  expDate: string | null;
  notes: string;
};

export type ManifestDirectorySyncResult = {
  added: number;
  alreadyPresent: number;
};

function errorMessage(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string' ? item.msg : ''))
      .filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  return 'Your submission could not be accepted. Review the form and try again.';
}

function directoryMemberRequest(payload: TeamMemberInput) {
  const {
    id: _id,
    updatedAt: _updatedAt,
    narExpiration,
    traExpiration,
    l2ExamDate,
    membershipExpiration,
    ...member
  } = payload as TeamMemberInput & Partial<TeamMember>;
  return {
    ...member,
    narExpiration: narExpiration || null,
    traExpiration: traExpiration || null,
    l2ExamDate: l2ExamDate || null,
    membershipExpiration: membershipExpiration || null,
  };
}

function directoryTeamRequest(payload: TeamAccountInput) {
  const {
    id: _id,
    additionalLeads: _additionalLeads,
    isSystem: _isSystem,
    updatedAt: _updatedAt,
    membershipStart,
    membershipEnd,
    primaryTeamLeadExpiration,
    teamLeadVerifiedAt: _teamLeadVerifiedAt,
    teamLeadVerifiedByEmail: _teamLeadVerifiedByEmail,
    ...team
  } = payload as TeamAccountInput & Partial<TeamAccount>;
  return {
    ...team,
    membershipStart: membershipStart || null,
    membershipEnd: membershipEnd || null,
    primaryTeamLeadExpiration: primaryTeamLeadExpiration || null,
  };
}

function directoryAdditionalLeadRequest(payload: TeamAdditionalLeadInput) {
  const { id: _id, updatedAt: _updatedAt, expirationDate, ...lead } = payload as TeamAdditionalLeadInput & Partial<TeamAdditionalLead>;
  return { ...lead, expirationDate: expirationDate || null };
}

function launchEventRequest(payload: LaunchEventInput) {
  const { id: _id, updatedAt: _updatedAt, ...event } = payload as LaunchEventInput & Partial<LaunchEvent>;
  return event;
}

async function readApi<T>(response: Response): Promise<T> {
  if (response.ok) return response.json() as Promise<T>;
  const body = await response.json().catch(() => null) as { detail?: unknown } | null;
  throw new Error(errorMessage(body?.detail));
}

export async function saveSubmission(
  kind: FormKind,
  payload: unknown,
  honeypot: string,
  idempotencyKey: string,
): Promise<SaveResult> {
  try {
    const response = await fetch(`/urrg/api/submissions/${kind}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify({ payload, honeypot }),
    });

    if (response.ok) {
      const body = await response.json() as { reference_id: string };
      return { mode: 'server', referenceId: body.reference_id };
    }

    const body = await response.json().catch(() => null) as { detail?: unknown } | null;
    return { mode: 'error', message: errorMessage(body?.detail) };
  } catch {
    return { mode: 'error', message: 'The submission service is unavailable. Please try again shortly.' };
  }
}

export async function getTeamAccessConfig(): Promise<{ turnstileSiteKey: string }> {
  return readApi<{ turnstileSiteKey: string }>(await fetch('/urrg/api/team-access/config'));
}

export async function requestTeamAccess(email: string, turnstileToken: string, returnTo = ''): Promise<string> {
  const body = await readApi<{ message: string }>(await fetch('/urrg/api/team-access/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, turnstileToken, returnTo }),
  }));
  return body.message;
}

export async function verifyTeamAccess(token: string): Promise<void> {
  await readApi(await fetch('/urrg/api/team-access/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  }));
}

export async function logoutTeamAccess(): Promise<void> {
  const response = await fetch('/urrg/api/team-access/logout', { method: 'POST' });
  if (response.status === 204) return;
  await readApi(response);
}

export async function getTeamDirectoryAccess(): Promise<TeamDirectoryAccess | null> {
  const response = await fetch('/urrg/api/team-directory/me');
  if (response.status === 401) return null;
  return readApi<TeamDirectoryAccess>(response);
}

export async function getTeamDirectorySummary(): Promise<TeamDirectorySummary> {
  return readApi<TeamDirectorySummary>(await fetch('/urrg/api/team-directory/summary'));
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  const body = await readApi<{ admins: AdminUser[] }>(await fetch('/urrg/api/admins'));
  return body.admins;
}

export async function createAdminUser(email: string): Promise<AdminUser> {
  return readApi<AdminUser>(await fetch('/urrg/api/admins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  }));
}

export async function deleteAdminUser(adminId: string): Promise<void> {
  const response = await fetch(`/urrg/api/admins/${adminId}`, { method: 'DELETE' });
  if (response.status === 204) return;
  await readApi(response);
}

export async function getLaunchDaySummary(): Promise<LaunchDaySummary> {
  return readApi<LaunchDaySummary>(await fetch('/urrg/api/launch-day-summary'));
}

export async function listLaunchEvents(): Promise<LaunchEvent[]> {
  const body = await readApi<{ events: LaunchEvent[] }>(await fetch('/urrg/api/launch-events'));
  return body.events;
}

export async function listLaunchOperationEvents(): Promise<LaunchEvent[]> {
  const body = await readApi<{ events: LaunchEvent[] }>(await fetch('/urrg/api/launch-operations/events'));
  return body.events;
}

export async function createLaunchEvent(payload: LaunchEventInput): Promise<LaunchEvent> {
  return readApi<LaunchEvent>(await fetch('/urrg/api/launch-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(launchEventRequest(payload)),
  }));
}

export async function updateLaunchEvent(eventId: string, payload: LaunchEventInput): Promise<LaunchEvent> {
  return readApi<LaunchEvent>(await fetch(`/urrg/api/launch-events/${eventId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(launchEventRequest(payload)),
  }));
}

export async function listLaunchSubmissions(eventId: string, page = 1): Promise<LaunchSubmissionList> {
  return readApi<LaunchSubmissionList>(await fetch(`/urrg/api/launch-events/${eventId}/certifications?page=${page}&page_size=50`));
}

export async function getLaunchSubmission(eventId: string, referenceId: string): Promise<LaunchSubmissionDetail> {
  return readApi<LaunchSubmissionDetail>(await fetch(`/urrg/api/launch-events/${eventId}/certifications/${referenceId}`));
}

export async function listLaunchFlightCards(eventId: string, page = 1): Promise<LaunchFlightCardList> {
  return readApi<LaunchFlightCardList>(await fetch(`/urrg/api/launch-events/${eventId}/flight-cards?page=${page}&page_size=50`));
}

export async function getLaunchFlightCard(eventId: string, referenceId: string): Promise<LaunchFlightCardDetail> {
  return readApi<LaunchFlightCardDetail>(await fetch(`/urrg/api/launch-events/${eventId}/flight-cards/${referenceId}`));
}

export async function updateFlightReview(eventId: string, referenceId: string, payload: FlightReviewInput): Promise<LaunchFlightCardDetail> {
  return readApi<LaunchFlightCardDetail>(await fetch(`/urrg/api/launch-events/${eventId}/flight-cards/${referenceId}/result`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }));
}

export async function updateCertificationReview(eventId: string, referenceId: string, payload: CertificationReviewInput): Promise<LaunchSubmissionDetail> {
  return readApi<LaunchSubmissionDetail>(await fetch(`/urrg/api/launch-events/${eventId}/certifications/${referenceId}/decision`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }));
}

export async function getCertificationSigners(eventId: string): Promise<CertificationSignerDirectory> {
  return readApi<CertificationSignerDirectory>(await fetch(`/urrg/api/launch-events/${eventId}/certification-signers`));
}

export async function saveTemporaryCertificationAuthorization(eventId: string, signerId: string, notes: string): Promise<CertificationSignerDirectory> {
  return readApi<CertificationSignerDirectory>(await fetch(`/urrg/api/launch-events/${eventId}/temporary-certification-authorization`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signerId, notes }),
  }));
}

export async function removeTemporaryCertificationAuthorization(eventId: string): Promise<void> {
  const response = await fetch(`/urrg/api/launch-events/${eventId}/temporary-certification-authorization`, { method: 'DELETE' });
  if (response.status === 204) return;
  await readApi(response);
}

export async function listDirectoryTeams(): Promise<TeamAccount[]> {
  const body = await readApi<{ teams: TeamAccount[] }>(await fetch('/urrg/api/team-directory/teams'));
  return body.teams;
}

export async function createDirectoryTeam(payload: TeamAccountInput): Promise<TeamAccount> {
  return readApi<TeamAccount>(await fetch('/urrg/api/team-directory/teams', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(directoryTeamRequest(payload)),
  }));
}

export async function updateDirectoryTeam(teamId: string, payload: TeamAccountInput): Promise<TeamAccount> {
  return readApi<TeamAccount>(await fetch(`/urrg/api/team-directory/teams/${teamId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(directoryTeamRequest(payload)),
  }));
}

export async function verifyDirectoryTeam(teamId: string): Promise<TeamAccount> {
  return readApi<TeamAccount>(await fetch(`/urrg/api/team-directory/teams/${teamId}/verify`, {
    method: 'POST',
  }));
}

export async function createDirectoryAdditionalLead(teamId: string, payload: TeamAdditionalLeadInput): Promise<TeamAccount> {
  return readApi<TeamAccount>(await fetch(`/urrg/api/team-directory/teams/${teamId}/additional-leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(directoryAdditionalLeadRequest(payload)),
  }));
}

export async function updateDirectoryAdditionalLead(teamId: string, leadId: string, payload: TeamAdditionalLeadInput): Promise<TeamAccount> {
  return readApi<TeamAccount>(await fetch(`/urrg/api/team-directory/teams/${teamId}/additional-leads/${leadId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(directoryAdditionalLeadRequest(payload)),
  }));
}

export async function deleteDirectoryAdditionalLead(teamId: string, leadId: string): Promise<TeamAccount> {
  return readApi<TeamAccount>(await fetch(`/urrg/api/team-directory/teams/${teamId}/additional-leads/${leadId}`, {
    method: 'DELETE',
  }));
}

export async function listDirectoryMembers(teamId: string): Promise<TeamMember[]> {
  const body = await readApi<{ members: TeamMember[] }>(await fetch(`/urrg/api/team-directory/teams/${teamId}/members`));
  return body.members;
}

export async function createDirectoryMember(teamId: string, payload: TeamMemberInput): Promise<TeamMember> {
  return readApi<TeamMember>(await fetch(`/urrg/api/team-directory/teams/${teamId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(directoryMemberRequest(payload)),
  }));
}

export async function updateDirectoryMember(teamId: string, memberId: string, payload: TeamMemberInput): Promise<TeamMember> {
  return readApi<TeamMember>(await fetch(`/urrg/api/team-directory/teams/${teamId}/members/${memberId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(directoryMemberRequest(payload)),
  }));
}

export async function deleteDirectoryMember(teamId: string, memberId: string): Promise<void> {
  const response = await fetch(`/urrg/api/team-directory/teams/${teamId}/members/${memberId}`, { method: 'DELETE' });
  if (response.status === 204) return;
  await readApi(response);
}

export async function previewDirectoryMemberImport(teamId: string, file: File): Promise<CsvImportPreview> {
  const formData = new FormData();
  formData.append('file', file);
  return readApi<CsvImportPreview>(await fetch(`/urrg/api/team-directory/teams/${teamId}/members/import-preview`, {
    method: 'POST',
    body: formData,
  }));
}

export async function applyDirectoryMemberImport(teamId: string, importId: string): Promise<{ added: number; updated: number }> {
  return readApi<{ added: number; updated: number }>(await fetch(
    `/urrg/api/team-directory/teams/${teamId}/members/imports/${importId}/apply`,
    { method: 'POST' },
  ));
}

export async function syncManifestDirectoryMembers(
  teamId: string,
  payload: {
    referenceId: string;
    flyers: ManifestDirectoryMember[];
    nonFlyers: ManifestDirectoryMember[];
  },
): Promise<ManifestDirectorySyncResult> {
  return readApi<ManifestDirectorySyncResult>(await fetch(`/urrg/api/team-directory/teams/${teamId}/manifest-members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }));
}
